/**
 * CON CUAL LLAVE ENTRA UNA CONEXION OAUTH (OAUTH-ELEGIR-LLAVE-T1, ASSIGN de Ares #25372).
 *
 * DE DONDE SALE. Work (Atenea), 11-oct: creo la conexion "Nexo OAuth (Atenea)" y la aprobacion decia "Entraria como
 * CHATGPT-JOSSE-001" sin dejar elegir; Josse no aprobo. La API elige por defecto la llave de CHATGPT-JOSSE-001 y ya acepta
 * otra (credentialId en POST /oauth/authorize/decision). La solicitud NO trae el sujeto: se deduce de la llave propuesta
 * (credencialPropuestaId) y se ofrecen las llaves VIVAS de esa misma persona, la propuesta primero y preseleccionada.
 * Que la elegida sea de ese sujeto lo valida la API (parte de A); el panel no ofrece llaves de otra persona.
 *
 * Sin DOM: lo prueba scripts/check-oauth-elegir-llave.mjs.
 */

/** Viva = ni quitada ni vencida (la misma regla de la lista de #/contexto). */
export function estaViva(c, ahora = Date.now()) {
  if (!c || c.revocada) return false;
  const v = c.vence ? new Date(c.vence).getTime() : NaN;
  return Number.isNaN(v) || v > ahora;
}

/**
 * Las llaves que se pueden elegir y cual va marcada.
 * @param {object[]} creds  filas de ContextApiClient.listCredentials (id, agente, nombre, prefijo, permisos, sujeto, revocada, vence)
 * @param {string} propuestaId  credencialPropuestaId de la solicitud
 * @returns {{lista: object[], elegida: string, sujeto: string, propuestaEncontrada: boolean}}
 */
export function candidatasOAuth(creds, propuestaId, ahora = Date.now()) {
  const vivas = (Array.isArray(creds) ? creds : []).filter((c) => estaViva(c, ahora));
  const propuesta = vivas.find((c) => c.id === propuestaId) || null;
  // Sin la propuesta no hay sujeto de donde colgarse: no se inventa una lista de llaves ajenas.
  if (!propuesta) return { lista: [], elegida: '', sujeto: '', propuestaEncontrada: false };
  const sujeto = propuesta.sujeto || '';
  // Misma regla que la API (nexo-api 480, OAuthReglas.ElegidaPermitida): otra llave entra solo si tiene el MISMO sujeto,
  // no nulo, que la propuesta. Con una propuesta sin sujeto (atada a un negocio) no se ofrece ninguna otra: la API la
  // rechazaria con credencial_de_otro_sujeto.
  const mismas = sujeto ? vivas.filter((c) => c.id !== propuesta.id && c.sujeto === sujeto) : [];
  const orden = (a, b) => a.agente.localeCompare(b.agente) || String(b.creada || '').localeCompare(String(a.creada || ''));
  return { lista: [propuesta, ...mismas.sort(orden)], elegida: propuesta.id, sujeto, propuestaEncontrada: true };
}

/**
 * LO QUE CADA LLAVE DICE EN LA LISTA (OAUTH-ELEGIR-LLAVE-T1 mejora, Ares #27303). El 14-oct a las 22:00 la reconexion de
 * ChatGPT quedo con la llave de 10 permisos SIN negocio aunque la API proponia la de 13 atada a EC (Q #27304: las 4 vivas
 * eran de la misma persona y los nombres se parecian). Ahora cada opcion dice si es la propuesta, cuantos permisos tiene
 * y de que negocio es; el nombre del negocio sale de la sesion solo si coincide con el de la llave.
 */
export function etiquetaDeLlave(c, propuestaId, negocioSesion = { id: '', nombre: '' }) {
  const o = c || {};
  const n = Array.isArray(o.permisos) ? o.permisos.length : 0;
  const neg = String(o.negocio || '');
  const negocio = !neg ? 'sin negocio'
    : (negocioSesion && negocioSesion.id === neg && negocioSesion.nombre ? 'del negocio ' + negocioSesion.nombre : 'de un negocio (' + neg.slice(0, 12) + '…)');
  return { propuesta: !!o.id && o.id === propuestaId, permisos: n, textoPermisos: n + (n === 1 ? ' permiso' : ' permisos'), negocio, tieneNegocio: !!neg };
}

/**
 * EL AVISO SI SE MARCA OTRA CON MENOS (Ares #27303). Vacio si es la propuesta o si no pierde nada. Dice cuantos permisos
 * se pierden, cuales (los primeros), y si ademas se deja el negocio de la propuesta.
 */
export function avisoDeEleccion(elegida, propuesta) {
  if (!elegida || !propuesta || elegida.id === propuesta.id) return '';
  const de = Array.isArray(propuesta.permisos) ? propuesta.permisos : [];
  const con = new Set(Array.isArray(elegida.permisos) ? elegida.permisos : []);
  const pierde = de.filter((p) => !con.has(p));
  const sinNegocio = !!propuesta.negocio && !elegida.negocio;
  if (!pierde.length && !sinNegocio) return '';
  const partes = [];
  if (pierde.length) {
    partes.push('Esta llave tiene ' + (con.size) + ' permisos y la propuesta ' + de.length + ': ChatGPT podra menos (pierde ' + pierde.length + ': '
      + pierde.slice(0, 4).join(', ') + (pierde.length > 4 ? ' y ' + (pierde.length - 4) + ' mas' : '') + ').');
  }
  if (sinNegocio) partes.push('Ademas no esta atada a ningun negocio: no lee los datos del negocio de la propuesta.');
  return partes.join(' ') + ' Si no es lo que quieres, vuelve a marcar la propuesta.';
}

/** "Entrara como WORK-ATENEA-001 con 3 permisos". */
export function fraseDeEntrada(c) {
  if (!c) return '';
  const n = Array.isArray(c.permisos) ? c.permisos.length : 0;
  return `Entrara como ${c.agente || '(sin agente)'} con ${n} ${n === 1 ? 'permiso' : 'permisos'}`;
}

/**
 * La vista previa recalculada con la llave ELEGIDA (#42497 de A), o null si no se le puede creer: que fallo, o que la
 * API la ignoro y respondio con la propuesta (credencialPropuestaId distinto de la elegida). Con null la vista dice lo
 * que la llave TIENE, como antes; nunca muestra lo que recibiria otra llave como si fuera de la elegida.
 * @param {{ ok:boolean, credencialPropuestaId?:string, recibiria?:string[] }} r  lo que devolvio leerSolicitud(id, elegida)
 * @param {string} elegida
 */
export function vistaPreviaDeLaElegida(r, elegida) {
  if (!r?.ok || !elegida || r.credencialPropuestaId !== elegida) return null;
  return r;
}
